import { inject, Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { RegistrationService } from '../services/registration.service';

/**
 * Bloque l'élève dont l'inscription en ligne n'est pas encore validée.
 *
 * <p>Le compte Keycloak est actif dès la soumission — l'élève définit son mot
 * de passe et se connecte — mais l'accès aux contenus n'est ouvert qu'une fois
 * le virement vérifié par l'administration. Ce garde renvoie vers la page
 * « compte en attente », qui porte le dépôt du justificatif.</p>
 *
 * <p>Les élèves créés par l'administration n'ont pas de demande : le back
 * répond 404 et le garde laisse passer. Aucune régression pour l'existant.</p>
 */
@Injectable({ providedIn: 'root' })
export class RegistrationStatusGuard implements CanActivate {
  private readonly registrations = inject(RegistrationService);
  private readonly router = inject(Router);

  /**
   * Seules les issues positives sont mémorisées : une demande encore en
   * attente doit être re-vérifiée à chaque navigation, faute de quoi une
   * validation par l'admin resterait sans effet jusqu'à la reconnexion.
   */
  private cleared = false;

  async canActivate(): Promise<boolean | UrlTree> {
    if (this.cleared) return true;

    const allowed = await this.isAllowed();
    if (allowed) {
      this.cleared = true;
      return true;
    }

    return this.router.createUrlTree(['/registration-pending']);
  }

  /**
   * Une erreur réseau ne doit pas se lire comme « inscription non validée » :
   * en cas de doute on laisse passer, l'utilisateur verra les vraies erreurs
   * plutôt qu'un diagnostic inventé.
   */
  private isAllowed(): Promise<boolean> {
    return firstValueFrom(
      this.registrations.getMine().pipe(
        map((res) => res?.data?.status === 'validated'),
        // 404 = compte créé par l'administration, hors parcours d'inscription.
        catchError(() => of(true)),
      ),
    );
  }

  /** À appeler après une validation, pour forcer une nouvelle vérification. */
  reset(): void {
    this.cleared = false;
  }
}
